import React, { useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import CSVColumnSelector from './CSVColumnSelector';
import Papa from 'papaparse';
import { deleteSegments } from '../../services/segmentService';
import uploadIcon from '../../assets/icons/upload_icon.svg';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const FileUpload = forwardRef(({
  onFileSelect, 
  className = '', 
  evaluationId, 
  segments = [], 
  onSourceLanguageChange, 
  onTargetLanguageChange,
  setIsSourceAutoDetected,
  setIsTargetAutoDetected,
  isMetricsMode = false
}, ref) => {
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [csvData, setCsvData] = useState(null);
  const [fileName, setFileName] = useState('');
  const [selectedColumns, setSelectedColumns] = useState(null);
  const [fileSize, setFileSize] = useState('');
  const [error, setError] = useState('');
  const [noHeaders, setNoHeaders] = useState(false);
  const [originalFile, setOriginalFile] = useState(null);
  const [modalKey, setModalKey] = useState(0);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const onDrop = useCallback((acceptedFiles) => {
    setError('');
    const file = acceptedFiles[0];
    
    if (file.size > MAX_FILE_SIZE) {
      setError(`<b>The file is too large</b>. Please reduce the file size below 10MB and try again`);
      return;
    }

    if (file) {
      setFileName(file.name);
      setFileSize(formatFileSize(file.size));
      setOriginalFile(file);
      setModalKey(prev => prev + 1);
      Papa.parse(file, {
        complete: (results) => {
          if (results.data && results.data.length > 0) {
            let headers, data;
            if (noHeaders) {
              headers = results.data[0];
              data = results.data;
            } else {
              headers = results.data[0];
              data = results.data.slice(1);
            }
            setCsvData({
              headers,
              data
            });
            setShowColumnSelector(true);
          }
        },
        header: false,
        skipEmptyLines: true,
        escapeChar: "\\",
        newline: "\n",
      });
    }
  }, [noHeaders]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv']
    },
    maxFiles: 1,
    noClick: true,
    noKeyboard: true
  });

  useImperativeHandle(ref, () => ({
    open
  }));

  const handleColumnSelect = (selection) => {
    setSelectedColumns(selection);
    const { sourceColumn, targetColumn, referenceColumn } = selection;
    
    const getOriginalColumnName = (columnName) => {
      return columnName.replace(/\s*\(\d+\)$/, '');
    };
    
    const sourceIndex = csvData.headers.findIndex(h => getOriginalColumnName(h) === sourceColumn);
    const targetIndex = csvData.headers.findIndex(h => getOriginalColumnName(h) === targetColumn);
    const referenceIndex = referenceColumn ? csvData.headers.findIndex(h => getOriginalColumnName(h) === referenceColumn) : -1;
  
    const processedData = csvData.data.map(row => {
      // Create an object with all columns from the CSV
      const rowData = {};
      csvData.headers.forEach((header, index) => {
        const originalHeader = getOriginalColumnName(header);
        rowData[originalHeader] = row[index];
      });
      
      const result = {
        ...rowData,
        source: row[sourceIndex],
        target: row[targetIndex]
      };
      
      if (isMetricsMode && referenceIndex !== -1) {
        result.reference = row[referenceIndex];
      }
      
      return result;
    });

    onFileSelect(processedData);
  };

  const handleRemove = async () => {
    const idsToDelete = segments
      .filter(segment => segment.serverId)
      .map(segment => segment.serverId);

    if (idsToDelete.length > 0 && evaluationId) {
      try {
        await deleteSegments(evaluationId, idsToDelete)
      } catch (error) {
        console.error('Failed to delete segments:', error);
      }
    }

    setShowColumnSelector(false);
    setSelectedColumns(null);
    setFileName('');
    setFileSize('');
    setError('');
    setModalKey(prev => prev + 1);
    onFileSelect([]);
    onSourceLanguageChange('');
    onTargetLanguageChange('');
    setIsTargetAutoDetected(false);
    setIsSourceAutoDetected(false);
  };

  const handleNoHeadersChange = (checked) => {
    setNoHeaders(checked);
    if (fileName && originalFile) {
      Papa.parse(originalFile, {
        complete: (results) => {
          if (results.data && results.data.length > 0) {
            let headers, data;
            if (checked) {
              headers = results.data[0];
              data = results.data;
            } else {
              headers = results.data[0];
              data = results.data.slice(1);
            }
            setCsvData({
              headers,
              data
            });
          }
        },
        header: false
      });
    }
  };

  return (
    <div className={className + ' border border-[#E2E8F0] p-[10px] rounded-lg bg-[#F9FAFB]'}>
      {!selectedColumns ? (
        <div
          {...getRootProps({ onClick: open })}
          className={`cursor-pointer border-2 border-dashed border-[#E2E8F0] rounded-[12px] bg-[#F9FAFB] p-0 min-h-[160px] flex flex-col items-center justify-center transition-colors
            ${isDragActive ? 'border-blue-500 bg-blue-50' : 'hover:border-[#CBD5E1]'}`}
          style={{ width: '100%' }}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center justify-center w-full py-8">
            <img src={uploadIcon} alt="Upload" className="w-10 h-10 mb-4" />
            <p className="text-base text-[#6B7280] mb-4 text-center">Drag and drop a CSV file, or click to select</p>
            <button
              type="button"
              className="bg-white hover:bg-[#F1F5F9] text-[#020817] font-semibold rounded-md px-6 py-2 text-base mb-4 border border-[#E2E8F0] shadow-[0px_1px_2px_0px_#0000000D] focus:outline-none focus:ring-0"
              onClick={e => { e.preventDefault(); e.stopPropagation(); open(); }}
            >
              Browse Files
            </button>
            <p className="text-sm text-[#9CA3AF]">Only CSV files are supported</p>
            {error && (
              <div className="mt-2 text-sm text-red-500" dangerouslySetInnerHTML={{ __html: error }} />
            )}
          </div>
        </div>
      ) : (
        <div className="p-2 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium">{fileName}</span>
              <span className="text-xs text-gray-500">
                (Source: {selectedColumns.sourceColumn}, Target: {selectedColumns.targetColumn}
                {isMetricsMode && selectedColumns.referenceColumn && `, Reference: ${selectedColumns.referenceColumn}`}, Size: {fileSize})
              </span>
            </div>
            <button
              onClick={handleRemove}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <CSVColumnSelector
        key={modalKey}
        isOpen={showColumnSelector}
        onClose={() => setShowColumnSelector(false)}
        onSave={handleColumnSelect}
        columns={csvData?.headers || []}
        csvData={csvData}
        fileName={fileName}
        onSourceLanguageChange={onSourceLanguageChange}
        onTargetLanguageChange={onTargetLanguageChange}
        onNoHeadersChange={handleNoHeadersChange}
        setIsSourceAutoDetected={setIsSourceAutoDetected}
        setIsTargetAutoDetected={setIsTargetAutoDetected}
        isMetricsMode={isMetricsMode}
      />
    </div>
  );
});

export default FileUpload; 